import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import FeedbackModal from './FeedbackModal';
import './DebateRoomMobile.css';

const API = process.env.REACT_APP_BACKEND_URL;

const DebateRoomMobile = () => {
  const { id: debateId } = useParams();
  const navigate = useNavigate();

  // Auto-resize textarea basierend auf Inhalt
  const handleTextareaChange = (e) => {
    const value = e.target.value;
    const textarea = e.target;
    
    // If empty, reset to single line immediately
    if (value.trim() === '') {
      textarea.style.height = '24px';
      textarea.style.overflowY = 'hidden';
      textarea.classList.remove('scrollable');
      textarea.scrollTop = 0;
      setNewArgument(value);
      setShowSendButton(false);
      return;
    }
    
    // Save current scroll and cursor state BEFORE changing height
    const previousScrollTop = textarea.scrollTop;
    const cursorPosition = textarea.selectionStart;
    const isTypingAtEnd = cursorPosition >= (newArgument?.length || 0);
    
    // Reset height to calculate scrollHeight
    textarea.style.height = '24px';
    
    // Calculate new height
    const scrollHeight = textarea.scrollHeight;
    const lineHeight = 24;
    const maxLines = 4;
    const maxHeight = lineHeight * maxLines;
    
    const needsScroll = scrollHeight > maxHeight;
    
    if (!needsScroll) {
      // Less than 4 lines - grow with content, no scroll needed
      textarea.style.height = Math.max(24, scrollHeight) + 'px';
      textarea.style.overflowY = 'hidden';
      textarea.classList.remove('scrollable');
      // No scroll needed, reset to top
      textarea.scrollTop = 0;
    } else {
      // 4+ lines - fixed height with scroll
      textarea.style.height = maxHeight + 'px';
      textarea.style.overflowY = 'auto';
      textarea.classList.add('scrollable');
      
      // Restore scroll position or scroll naturally
      requestAnimationFrame(() => {
        if (isTypingAtEnd) {
          // Typing at end - let browser handle natural scroll to keep cursor visible
          // This preserves WhatsApp-like behavior
          const newScrollHeight = textarea.scrollHeight;
          const visibleHeight = maxHeight;
          // Show last 3 lines (keep cursor visible, show some context)
          textarea.scrollTop = Math.max(0, newScrollHeight - visibleHeight);
        } else {
          // Editing in middle - preserve scroll position
          textarea.scrollTop = previousScrollTop;
        }
      });
    }
    
    // Handle button state
    setNewArgument(value);
    if (value.length > 0) {
      setShowSendButton(true);
      if (!hasStartedTyping) {
        setHasStartedTyping(true);
        setIsTimerRunning(true);
        setDebateStarted(true);
      }
    } else {
      setShowSendButton(false);
    }
  };

  const textareaRef = useRef(null);
  
  // Initialize textarea height on mount
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = '24px'; // Force single line on load
      console.log('Textarea initialized with height:', textarea.style.height);
    }
  }, []);
  const timersInitializedRef = useRef(false);
  const recognitionRef = useRef(null);

  // Debate State
  const [debate, setDebate] = useState(null);
  const [debateArguments, setDebateArguments] = useState([]);
  const [newArgument, setNewArgument] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [currentTurn, setCurrentTurn] = useState('a');

  // Timer State
  const [timeRemainingA, setTimeRemainingA] = useState(120);
  const [timeRemainingB, setTimeRemainingB] = useState(120);
  const [totalTimeRemaining, setTotalTimeRemaining] = useState(1800);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [totalTimeStarted, setTotalTimeStarted] = useState(false);
  const [timeUpShown, setTimeUpShown] = useState(false);

  // UI State
  const [playerAName, setPlayerAName] = useState('Protagonist A');
  const [playerBName, setPlayerBName] = useState('Protagonist B');
  const [hasStartedTyping, setHasStartedTyping] = useState(false);
  const [expandedAnalysis, setExpandedAnalysis] = useState(null);
  const [analysisFontSize, setAnalysisFontSize] = useState(14);
  const [shouldResetTimer, setShouldResetTimer] = useState(false);
  const [inputMode, setInputMode] = useState('keyboard');
  const [isRecording, setIsRecording] = useState(false);
  const [showSendButton, setShowSendButton] = useState(false);
  const [debateStarted, setDebateStarted] = useState(false);
  
  // Feedback Modal State
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [selectedArgumentForFeedback, setSelectedArgumentForFeedback] = useState(null);

  // Pause/Resume State (Pro/Unlimited Feature)
  const [userTier, setUserTier] = useState('free');
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [showResumeOverlay, setShowResumeOverlay] = useState(false);
  const [resumeLink, setResumeLink] = useState('');
  const [isPausing, setIsPausing] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [daysRemaining, setDaysRemaining] = useState(7);

  // AI State
  const [aiStatus, setAiStatus] = useState({ show: false, phase: 'thinking', remaining: 0, total: 0 });

  // Asset URLs
  const backgroundUrl = 'https://customer-assets.emergentagent.com/job_debatefy-freemium/artifacts/m0t4gk3h_dabatefy_dabateroom_background_ND02.jpg';
  const headerUrl = 'https://customer-assets.emergentagent.com/job_debatefy-freemium/artifacts/acxtk31p_dabatefy_dabateroom_Element_A_ND02.png';
  const infoBoxUrl = 'https://customer-assets.emergentagent.com/job_argububbles/artifacts/kc1p1mfm_dabatefy_dabateroom_Element_A_small_ND.png';
  const sendIconUrl = 'https://customer-assets.emergentagent.com/job_argububbles/artifacts/lhmgs1er_Send.svg';

  // Format time helper
  const formatTime = (seconds) => {
    if (seconds === null || seconds === undefined) return '00:00';
    const absSeconds = Math.abs(seconds);
    const mins = Math.floor(absSeconds / 60);
    const secs = Math.floor(absSeconds % 60);
    const sign = seconds < 0 ? '-' : '';
    return `${sign}${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Dedizierte Timer-Reset-Funktion
  const triggerTimerReset = () => {
    setShouldResetTimer(true);
  };

  // Fetch user tier on mount
  useEffect(() => {
    const fetchUserTier = async () => {
      const token = localStorage.getItem('user_session_token') || 
                    localStorage.getItem('admin_session_token') || 
                    localStorage.getItem('token');
      
      if (token) {
        try {
          const response = await axios.get(`${API}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUserTier(response.data.subscription_tier || 'free');
        } catch (error) {
          console.log('Could not fetch user tier:', error);
        }
      }
    };
    fetchUserTier();
  }, []);

  // Handle Pause Debate (Pro/Unlimited Feature)
  const handlePauseDebate = async () => {
    // Block non-Pro users completely
    if (!['pro', 'unlimited'].includes(userTier)) {
      toast.error('⭐ Upgrade auf Pro für diese Funktion', {
        description: 'Debatten pausieren ist ein Pro-Feature.'
      });
      return;
    }

    // Check for authentication token
    const token = localStorage.getItem('user_session_token') || 
                  localStorage.getItem('admin_session_token') || 
                  localStorage.getItem('token');
    
    if (!token) {
      toast.error('🔐 Login erforderlich', {
        description: 'Bitte einloggen um Debatten zu pausieren.'
      });
      return;
    }

    try {
      setIsPausing(true);

      const response = await axios.post(
        `${API}/api/debate/${debateId}/pause`,
        {
          time_remaining_a: timeRemainingA,
          time_remaining_b: timeRemainingB,
          total_time_remaining: totalTimeRemaining
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const link = `${window.location.origin}/debate/${debateId}`;
      setResumeLink(link);
      setShowPauseModal(true);
      setDaysRemaining(response.data.days_remaining);
      setLinkCopied(false);
      
      toast.info('⏸️ Debatte pausiert!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Fehler beim Pausieren';
      toast.error(message);
    } finally {
      setIsPausing(false);
    }
  };

  // Handle Resume Debate
  const handleResumeDebate = async () => {
    try {
      const token = localStorage.getItem('user_session_token') || 
                    localStorage.getItem('admin_session_token') || 
                    localStorage.getItem('token');
      
      const response = await axios.post(
        `${API}/api/debate/${debateId}/resume`,
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      if (response.data.time_remaining_a !== undefined) {
        setTimeRemainingA(response.data.time_remaining_a);
      }
      if (response.data.time_remaining_b !== undefined) {
        setTimeRemainingB(response.data.time_remaining_b);
      }
      if (response.data.total_time_remaining !== undefined) {
        setTotalTimeRemaining(response.data.total_time_remaining);
      }

      setShowResumeOverlay(false);
      toast.success('▶️ Debatte fortgesetzt!');
      fetchDebate();
      fetchArguments();
    } catch (error) {
      const message = error.response?.data?.detail || 'Fehler beim Fortsetzen';
      toast.error(message);
    }
  };

  // Fetch debate data
  const fetchDebate = async () => {
    try {
      const response = await axios.get(`${API}/api/debate/${debateId}`);
      const debateData = response.data;
      setDebate(debateData);
      setCurrentTurn(debateData.current_turn || 'a');

      // Check if debate is paused - show resume overlay (but not if we just paused it ourselves)
      if (debateData.status === 'paused' && !showPauseModal) {
        setShowResumeOverlay(true);
        // Don't continue setup until resumed
        return;
      } else if (debateData.status === 'active') {
        setShowResumeOverlay(false);
      }

      // Initialize timers only once
      if (!timersInitializedRef.current) {
        setTimeRemainingA(debateData.time_remaining_a || debateData.argument_time_limit || 120);
        setTimeRemainingB(debateData.time_remaining_b || debateData.argument_time_limit || 120);
        setTotalTimeRemaining(debateData.total_time_remaining || debateData.total_time_limit || 1800);
        timersInitializedRef.current = true;
      }

      // Set player names
      const myRole = localStorage.getItem('participant_role');
      if (myRole === 'protagonist_a') {
        setPlayerAName(localStorage.getItem('participant_name') || 'You');
      } else if (myRole === 'protagonist_b') {
        setPlayerBName(localStorage.getItem('participant_name') || 'You');
      }

      // Fetch opponent name
      const opponentSessionId = myRole === 'protagonist_a' ? debateData.session_b_id : debateData.session_a_id;
      if (opponentSessionId && debateData.is_ai_opponent && opponentSessionId.startsWith('ai_opponent_')) {
        const aiStyle = debateData.ai_style || "AI-Opponent";
        const cleanName = aiStyle.replace(/^🤖\s*/, '');
        const match = cleanName.match(/^(.+?)\s*\((.+?)\)$/);
        if (match) {
          if (myRole === 'protagonist_a') {
            setPlayerBName(match[1].trim());
          } else if (myRole === 'protagonist_b') {
            setPlayerAName(match[1].trim());
          }
        }
      }
    } catch (error) {
      console.error('Error fetching debate:', error);
      toast.error('Failed to load debate');
    }
  };

  // Fetch arguments
  const fetchArguments = async () => {
    try {
      const response = await axios.get(`${API}/api/debate/${debateId}/arguments`);
      const args = response.data;
      setDebateArguments(args);
    } catch (error) {
      console.error('Error fetching arguments:', error);
    }
  };

  // No scroll prevention needed - position: fixed + contain handles it

  // Initialize debate and polling - NO SCROLL on initial load
  useEffect(() => {
    timersInitializedRef.current = false;
    window.scrollTo(0, 0);
    
    fetchDebate();
    fetchArguments();
    const interval = setInterval(() => {
      fetchDebate();
      fetchArguments();
    }, 5000);
    return () => clearInterval(interval);
  }, [debateId]);

  // De-Coupling Input: Prevent browser auto-scroll on focus (Improved version)
  useEffect(() => {
    // Wait for elements to be ready
    const waitForElements = () => {
      const inputArea = document.getElementById('mobile-input-portal');
      const textarea = document.querySelector('.mobile-input-textarea');
      
      if (!inputArea || !textarea) {
        setTimeout(waitForElements, 100);
        return;
      }
      
      initializeInputHandlers(inputArea, textarea);
    };
    
    const initializeInputHandlers = (inputArea, textarea) => {
      if (!inputArea || !textarea) return;
      
      let scrollBlockerInterval = null;
      let viewportTracker = null;
      let isKeyboardOpen = false;
      
      // Function to reset all scrolls
      const resetAllScrolls = () => {
        if (window.scrollY !== 0) window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      };
      
      // Handle viewport changes (keyboard open/close)
      const handleViewportChange = () => {
        if (!window.visualViewport) return;
        
        const viewport = window.visualViewport;
        const viewportHeight = viewport.height;
        const windowHeight = window.innerHeight;
        const keyboardHeight = Math.max(0, windowHeight - viewportHeight - viewport.offsetTop);
        
        if (keyboardHeight > 100) {
          // Keyboard is open
          isKeyboardOpen = true;
          inputArea.style.bottom = `${keyboardHeight}px`;
          document.body.classList.add('keyboard-open');
          
          // Aggressively prevent page scroll when keyboard opens
          resetAllScrolls();
        } else {
          // Keyboard is closed
          isKeyboardOpen = false;
          inputArea.style.bottom = '0px';
          document.body.classList.remove('keyboard-open');
        }
      };
      
      // On focus: start monitoring and prevent scroll
      const handleFocus = (e) => {
        e.preventDefault();
        
        // Immediate scroll reset
        resetAllScrolls();
        
        // Start continuous scroll blocking for 1 second
        scrollBlockerInterval = setInterval(resetAllScrolls, 16);
        setTimeout(() => {
          if (scrollBlockerInterval) {
            clearInterval(scrollBlockerInterval);
            scrollBlockerInterval = null;
          }
        }, 1000);
        
        // Start viewport tracking
        viewportTracker = setInterval(handleViewportChange, 50);
        
        // Initial viewport check
        handleViewportChange();
      };
      
      // On blur: stop monitoring
      const handleBlur = () => {
        if (scrollBlockerInterval) {
          clearInterval(scrollBlockerInterval);
          scrollBlockerInterval = null;
        }
        if (viewportTracker) {
          clearInterval(viewportTracker);
          viewportTracker = null;
        }
        
        // Reset input position
        inputArea.style.bottom = '0px';
        document.body.classList.remove('keyboard-open');
      };
      
      // Prevent touchmove on body when keyboard is open
      const preventBodyScroll = (e) => {
        const target = e.target;
        // Allow scroll only in arguments container
        if (target.closest('.mobile-arguments-container') || target.closest('.mobile-input-textarea')) {
          return;
        }
        if (isKeyboardOpen) {
          e.preventDefault();
        }
      };
      
      // Add event listeners
      textarea.addEventListener('focus', handleFocus, { passive: false });
      textarea.addEventListener('blur', handleBlur);
      document.body.addEventListener('touchmove', preventBodyScroll, { passive: false });
      
      // VisualViewport listeners for fallback
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', handleViewportChange);
        window.visualViewport.addEventListener('scroll', handleViewportChange);
      }
      
      // Cleanup function
      return () => {
        textarea.removeEventListener('focus', handleFocus);
        textarea.removeEventListener('blur', handleBlur);
        document.body.removeEventListener('touchmove', preventBodyScroll);
        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', handleViewportChange);
          window.visualViewport.removeEventListener('scroll', handleViewportChange);
        }
        if (scrollBlockerInterval) clearInterval(scrollBlockerInterval);
        if (viewportTracker) clearInterval(viewportTracker);
      };
    };
    
    waitForElements();
    
    return () => {};
  }, []);

  // Hide Emergent label ONCE on mount (no periodic checks)
  useEffect(() => {
    setTimeout(() => {
      const allElements = document.querySelectorAll('*');
      allElements.forEach(el => {
        if (el.textContent?.includes('Made with Emergent')) {
          const rect = el.getBoundingClientRect();
          if (rect.width < 300 && rect.bottom > window.innerHeight - 200) {
            el.style.display = 'none';
          }
        }
      });
    }, 1000);
  }, []);

  // Initialize debate and polling - NO SCROLL on initial load
  useEffect(() => {
    timersInitializedRef.current = false;
    
    fetchDebate();
    fetchArguments();
    const interval = setInterval(() => {
      fetchDebate();
      fetchArguments();
    }, 5000);
    return () => clearInterval(interval);
  }, [debateId]);

  // Enhanced keyboard handling for phones & tablets
  useEffect(() => {
    if (!window.visualViewport) return;
    
    const handleViewport = () => {
      const inputArea = document.querySelector('.mobile-input-area');
      if (!inputArea) return;
      
      // Calculate keyboard height
      const viewportHeight = window.visualViewport.height;
      const windowHeight = window.innerHeight;
      const keyboardHeight = windowHeight - viewportHeight;
      
      // Only adjust if keyboard is significantly open (> 150px for tablets)
      const threshold = window.innerWidth > 600 ? 150 : 100;
      
      if (keyboardHeight > threshold) {
        // Keyboard is open - move input area up
        const safeBottom = Math.max(keyboardHeight, 0);
        inputArea.style.bottom = `${safeBottom}px`;
        inputArea.style.transition = 'bottom 0.2s ease-out';
        
        // Prevent body scroll when keyboard is open
        document.body.style.overflow = 'hidden';
      } else {
        // Keyboard is closed - reset position
        inputArea.style.bottom = '0px';
        inputArea.style.transition = 'bottom 0.2s ease-out';
        document.body.style.overflow = 'hidden';
      }
    };
    
    // Initial call
    handleViewport();
    
    // Listen to viewport changes
    window.visualViewport.addEventListener('resize', handleViewport);
    window.visualViewport.addEventListener('scroll', handleViewport);
    
    return () => {
      window.visualViewport.removeEventListener('resize', handleViewport);
      window.visualViewport.removeEventListener('scroll', handleViewport);
    };
  }, []);

  // Per-argument timer countdown
  useEffect(() => {
    if (!isTimerRunning) return;

    const myRole = localStorage.getItem('participant_role');
    const interval = setInterval(() => {
      if (myRole === 'protagonist_a') {
        setTimeRemainingA((prev) => Math.max(prev - 1, -999));
      } else if (myRole === 'protagonist_b') {
        setTimeRemainingB((prev) => Math.max(prev - 1, -999));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isTimerRunning]);

  // Total time countdown
  useEffect(() => {
    if (!totalTimeStarted) return;

    const interval = setInterval(() => {
      setTotalTimeRemaining((prev) => {
        const newTime = Math.max(prev - 1, 0);
        if (newTime === 0 && !timeUpShown) {
          toast.error('Debate time is up!');
          setTimeUpShown(true);
        }
        return newTime;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [totalTimeStarted, timeUpShown]);

  // Note: Auto-grow logic is handled by handleTextareaChange function

  // Initialize Speech Recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      
      recognition.continuous = false; // Changed to false to avoid repetitions
      recognition.interimResults = false; // Changed to false - only final results
      recognition.lang = 'de-DE';
      recognition.maxAlternatives = 1;
      
      let lastProcessedIndex = 0;
      
      recognition.onresult = (event) => {
        // Only process NEW results starting from lastProcessedIndex
        for (let i = lastProcessedIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            const transcript = event.results[i][0].transcript;
            // Append only the new final transcript
            setNewArgument(prev => {
              const newText = prev + (prev ? ' ' : '') + transcript.trim();
              return newText;
            });
            lastProcessedIndex = i + 1;
          }
        }
      };
      
      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        if (event.error === 'no-speech') {
          toast.error('Keine Sprache erkannt. Bitte sprechen Sie ins Mikrofon.');
        } else if (event.error === 'not-allowed') {
          toast.error('Mikrofon-Zugriff verweigert. Bitte erlauben Sie den Zugriff.');
        } else if (event.error === 'aborted') {
          // Silently ignore aborted errors
        } else {
          toast.error('Fehler bei der Spracherkennung: ' + event.error);
        }
        setIsRecording(false);
      };
      
      recognition.onend = () => {
        if (isRecording) {
          // Restart for continuous recording
          lastProcessedIndex = 0; // Reset index for new session
          try {
            recognition.start();
          } catch (e) {
            console.error('Error restarting recognition:', e);
          }
        }
      };
      
      recognitionRef.current = recognition;
    } else {
      console.warn('Speech Recognition not supported');
    }
    
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Ignore errors on cleanup
        }
      }
    };
  }, [isRecording]);

  // Handle microphone button click
  const handleMicrophoneClick = () => {
    if (!recognitionRef.current) {
      toast.error('Spracherkennung wird von diesem Browser nicht unterstützt.');
      return;
    }
    
    if (isRecording) {
      // Stop recording
      recognitionRef.current.stop();
      setIsRecording(false);
      setShowSendButton(newArgument.length > 0);
      toast.success('Aufnahme gestoppt');
    } else {
      // Start recording
      try {
        recognitionRef.current.start();
        setIsRecording(true);
        setShowSendButton(true);
        setHasStartedTyping(true);
        setIsTimerRunning(true);
        setDebateStarted(true);
        toast.info('🎤 Sprachaufnahme gestartet...');
      } catch (error) {
        console.error('Error starting speech recognition:', error);
        toast.error('Fehler beim Starten der Aufnahme');
      }
    }
  };

  // Reset timer after argument submission
  useEffect(() => {
    if (shouldResetTimer && debate?.argument_time_limit) {
      const myRole = localStorage.getItem('participant_role');
      const timerValue = debate.argument_time_limit;
      
      if (myRole === 'protagonist_a') {
        setTimeRemainingA(timerValue);
      } else if (myRole === 'protagonist_b') {
        setTimeRemainingB(timerValue);
      }
      
      setShouldResetTimer(false);
    }
  }, [shouldResetTimer, debate?.argument_time_limit]);

  // Submit argument
  const handleSubmitArgument = async () => {
    if (!newArgument.trim()) {
      toast.error('Please enter an argument');
      return;
    }

    const myRole = localStorage.getItem('participant_role');
    const mySessionId = localStorage.getItem('session_id');

    if (!myRole || !mySessionId) {
      toast.error('Session error. Please rejoin the debate.');
      return;
    }

    const isMyTurn = (currentTurn === 'a' && myRole === 'protagonist_a') || 
                     (currentTurn === 'b' && myRole === 'protagonist_b');

    if (!isMyTurn) {
      toast.error("It's not your turn!");
      return;
    }

    try {
      setSubmitting(true);
      setIsTimerRunning(false);
      
      const response = await axios.post(`${API}/api/debate/${debateId}/submit-argument`, {
        debate_id: debateId,
        session_id: mySessionId,
        content: newArgument,
        thinking_time_seconds: 0,
        typing_time_seconds: 0
      });
      
      if (response.status === 200 || response.status === 201) {
        toast.success('Argument submitted!');
        
        setNewArgument('');
        setHasStartedTyping(false);
        setIsTimerRunning(false);
        
        if (!totalTimeStarted) {
          setTotalTimeStarted(true);
        }
        
        await fetchDebate();
        await fetchArguments();
        
        triggerTimerReset();
        
        // CRITICAL: Keep textarea focused after submit to keep keyboard open
        setTimeout(() => {
          const textarea = document.querySelector('textarea.whatsapp-textarea');
          if (textarea && document.activeElement !== textarea) {
            textarea.focus();
          }
        }, 100);
      }
    } catch (error) {
      console.error('Error submitting argument:', error);
      let errorMessage = 'Failed to submit argument';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else {
          errorMessage = JSON.stringify(detail);
        }
      }
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (!debate) {
    return (
      <div className="mobile-debate-loading">
        <div>Loading debate...</div>
      </div>
    );
  }

  return (
    <div className="mobile-debate-container">
      {/* Background - 90° rotated */}
      <img 
        src={backgroundUrl} 
        alt="Background" 
        className="mobile-debate-background"
      />

      {/* TOP SECTION - Header + Thesis + Info Boxes as one unit */}
      <div className="mobile-top-section">
        {/* Header */}
        <div className="mobile-debate-header">
          <img src={headerUrl} alt="Header" className="mobile-header-bg" />
          <div className="mobile-header-content">
            <div className="mobile-player-name left">{playerAName}</div>
            <div className="mobile-total-time">
              <div className="mobile-time-label">Total Time</div>
              <div className="mobile-time-value">{formatTime(totalTimeRemaining)}</div>
            </div>
            <div className="mobile-player-name right">{playerBName}</div>
          </div>
          {/* Pause Button - Only visible for Pro/Unlimited users */}
          {['pro', 'unlimited'].includes(userTier) && (
            <button 
              className="mobile-pause-btn"
              onClick={handlePauseDebate}
              disabled={isPausing}
              title="Pausieren"
            >
              {isPausing ? '⏳' : '⏸️'}
            </button>
          )}
        </div>

        {/* Thesis Container - Slides down when debate starts */}
        <div className={`mobile-thesis ${debateStarted ? 'started' : ''}`}>
          <img src={headerUrl} alt="Thesis Background" className="mobile-thesis-bg" />
          <div className="mobile-thesis-content">
            <div className="mobile-thesis-text">{debate?.thesis || ''}</div>
          </div>
        </div>

        {/* Info Boxes - Behind thesis, revealed when thesis slides down */}
        {/* Order: Score A, Time A, Time B, Score B */}
        <div className="mobile-info-boxes">
        {/* Score A */}
        <div className={`mobile-info-box ${
          !debateStarted ? 'neutral' : 
          (debate?.score_a || 0) > 0 ? 'positive' : 
          (debate?.score_a || 0) < 0 ? 'negative' : 'neutral'
        }`}>
          <img src={infoBoxUrl} alt="Info Box" className="mobile-info-box-bg" />
          <div className="mobile-info-box-content">
            <div className="mobile-info-label">SCORE A:</div>
            <div className="mobile-info-value">
              {(debate?.score_a || 0) > 0 ? '+' : ''}{debate?.score_a || 0}
            </div>
          </div>
        </div>

        {/* Time A */}
        <div className={`mobile-info-box ${
          !debateStarted ? 'neutral' :
          timeRemainingA > 0 ? 'positive' : 
          timeRemainingA < 0 ? 'negative' : 'neutral'
        }`}>
          <img src={infoBoxUrl} alt="Info Box" className="mobile-info-box-bg" />
          <div className="mobile-info-box-content">
            <div className="mobile-info-label">TIME A:</div>
            <div className="mobile-info-value">{formatTime(timeRemainingA)}</div>
          </div>
        </div>

        {/* Time B */}
        <div className={`mobile-info-box ${
          !debateStarted ? 'neutral' :
          timeRemainingB > 0 ? 'positive' : 
          timeRemainingB < 0 ? 'negative' : 'neutral'
        }`}>
          <img src={infoBoxUrl} alt="Info Box" className="mobile-info-box-bg" />
          <div className="mobile-info-box-content">
            <div className="mobile-info-label">TIME B:</div>
            <div className="mobile-info-value">{formatTime(timeRemainingB)}</div>
          </div>
        </div>

        {/* Score B */}
        <div className={`mobile-info-box ${
          !debateStarted ? 'neutral' :
          (debate?.score_b || 0) > 0 ? 'positive' : 
          (debate?.score_b || 0) < 0 ? 'negative' : 'neutral'
        }`}>
          <img src={infoBoxUrl} alt="Info Box" className="mobile-info-box-bg" />
          <div className="mobile-info-box-content">
            <div className="mobile-info-label">SCORE B:</div>
            <div className="mobile-info-value">
              {(debate?.score_b || 0) > 0 ? '+' : ''}{debate?.score_b || 0}
            </div>
          </div>
        </div>
      </div>
      </div>
      {/* END TOP SECTION */}

      {/* Arguments */}
      <div className="mobile-arguments-container">
        {debateArguments.map((arg, index) => {
          const isMyArgument = arg.session_id === localStorage.getItem('session_id');
          const isProtagonistA = arg.participant_role === 'protagonist_a';
          const bubbleClass = isMyArgument ? 'bubble-right' : 'bubble-left';
          const argumentScore = arg.moderation_result?.score?.total_score || 0;
          const scoreClass = argumentScore > 0 ? 'positive' : argumentScore < 0 ? 'negative' : 'neutral';
          const summary = arg.moderation_result?.summary || '';
          const aiFeedback = arg.moderation_result?.ai_feedback || '';
          const showingAnalysis = expandedAnalysis === arg.id;

          return (
            <div 
              key={arg.id || index}
              className={`mobile-argument-bubble ${bubbleClass} ${showingAnalysis ? 'showing-analysis' : ''}`}
            >
              <div className="mobile-bubble-author">
                {isProtagonistA ? playerAName : playerBName}
              </div>
              
              {!showingAnalysis ? (
                <div className="mobile-bubble-content">{arg.content}</div>
              ) : (
                <div className="mobile-bubble-analysis" style={{ fontSize: `${analysisFontSize}px` }}>
                  <div className="mobile-analysis-header">
                    <span>Analyse</span>
                    <div className="mobile-font-controls">
                      <button onClick={(e) => { e.stopPropagation(); setAnalysisFontSize(prev => Math.min(prev + 2, 20)); }}>+</button>
                      <button onClick={(e) => { e.stopPropagation(); setAnalysisFontSize(prev => Math.max(prev - 2, 10)); }}>−</button>
                    </div>
                  </div>
                  <div className="mobile-analysis-score">
                    Score: <span className={scoreClass}>{argumentScore > 0 ? `+${argumentScore}` : argumentScore}</span>
                  </div>
                  {summary && <div className="mobile-analysis-section"><strong>Zusammenfassung:</strong> {summary}</div>}
                  {aiFeedback && <div className="mobile-analysis-section"><strong>Details:</strong> {aiFeedback}</div>}
                  
                  {/* Feedback Button */}
                  <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedArgumentForFeedback(arg);
                        setFeedbackModalOpen(true);
                      }}
                      style={{
                        padding: '6px 12px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        borderRadius: '4px',
                        color: '#000000',
                        cursor: 'pointer',
                        fontSize: '11px',
                        fontFamily: 'Roboto, sans-serif',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        justifyContent: 'center'
                      }}
                    >
                      <img 
                        src="https://customer-assets.emergentagent.com/job_mobile-tablet-desk/artifacts/ax0ktd0f_feedback_icon.svg"
                        alt="Feedback"
                        style={{ width: '36px', height: '36px' }}
                      />
                      Feedback
                    </button>
                  </div>
                </div>
              )}
              
              <button 
                className={`mobile-score-btn ${scoreClass}`}
                onClick={() => setExpandedAnalysis(showingAnalysis ? null : arg.id)}
              >
                <div className="mobile-score-value">{argumentScore > 0 ? `+${argumentScore}` : argumentScore}</div>
                <div className="mobile-score-label">{showingAnalysis ? 'BACK' : 'SCORE'}</div>
              </button>
            </div>
          );
        })}
      </div>

      {/* INPUT - Mobile Input Area mit integriertem Button - Rendered via PORTAL */}
      {createPortal(
        <div className="mobile-input-area" id="mobile-input-portal">
          <div className="mobile-input-container">
            <textarea
              ref={textareaRef}
              className="mobile-input-textarea"
              placeholder="Enter your argument..."
              value={newArgument}
              rows={1}
              onChange={handleTextareaChange}
              onFocus={(e) => {
                // Prevent default scroll-into-view behavior
                e.target.scrollIntoView = () => {};
              }}
              maxLength={500}
              disabled={submitting || aiStatus.show}
            />
            
            {!showSendButton && !isRecording ? (
              <button 
                className="mobile-input-button mic"
                onClick={handleMicrophoneClick}
                title="Voice input"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_debatefy-freemium/artifacts/9vfj6vbv_mic.svg" 
                  alt="Mic"
                  style={{width: '18px', height: '18px', filter: 'brightness(0) invert(1)'}}
                />
              </button>
            ) : isRecording ? (
              <button 
                className="mobile-input-button recording"
                onClick={handleMicrophoneClick}
                title="Stop recording"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_debatefy-freemium/artifacts/9vfj6vbv_mic.svg" 
                  alt="Recording"
                  style={{width: '18px', height: '18px', filter: 'brightness(0) invert(1)'}}
                />
              </button>
            ) : (
              <button 
                className="mobile-input-button send"
                onClick={handleSubmitArgument}
                disabled={submitting || !newArgument.trim()}
                title="Send argument"
              >
                <img 
                  src={sendIconUrl} 
                  alt="Send"
                  style={{width: '18px', height: '18px', filter: 'brightness(0) invert(1)'}}
                />
              </button>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={feedbackModalOpen}
        onClose={() => setFeedbackModalOpen(false)}
        argument={selectedArgumentForFeedback}
        debateId={debateId}
      />

      {/* Pause Success Modal */}
      {showPauseModal && (
        <div className="mobile-pause-overlay">
          <div className="mobile-pause-modal">
            <div className="mobile-pause-icon">⏸️</div>
            <h2>Debatte Pausiert</h2>
            <p>Teile diesen Link, um die Debatte fortzusetzen:</p>
            
            <div className="mobile-resume-link-box">
              <input 
                type="text" 
                value={resumeLink} 
                readOnly 
                className="mobile-resume-link-input"
              />
              <button 
                className={`mobile-copy-btn ${linkCopied ? 'copied' : ''}`}
                onClick={() => {
                  navigator.clipboard.writeText(resumeLink);
                  setLinkCopied(true);
                  toast.success('✅ Link kopiert!');
                }}
              >
                {linkCopied ? '✅' : '📋'}
              </button>
            </div>
            
            <p className="mobile-pause-expiry">
              ⏰ Gültig für <strong>{daysRemaining} Tage</strong>
            </p>
            
            {linkCopied && (
              <button 
                className="mobile-pause-home-btn"
                onClick={() => navigate('/')}
              >
                Zur Startseite
              </button>
            )}
          </div>
        </div>
      )}

      {/* Resume Overlay for Paused Debates */}
      {showResumeOverlay && (
        <div className="mobile-resume-overlay">
          <div className="mobile-resume-modal">
            <div className="mobile-resume-icon">⏸️</div>
            <h2>Pausierte Debatte</h2>
            <p>Diese Debatte wurde pausiert.</p>
            <p className="mobile-resume-thesis">&ldquo;{debate?.thesis}&rdquo;</p>
            
            <button 
              className="mobile-resume-btn"
              onClick={handleResumeDebate}
            >
              ▶️ Fortsetzen
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DebateRoomMobile;
